<template>
    <div class="commentBox" :class="{ personalNote: commentIsNote }">
        <div class="title" :style="{ color: [commentIsNote ? '' : colorClient] }">
            {{ commentTitle }} <span class="date">{{ comment.createdAt }}</span>
        </div>
        <div class="commentContent">
            <span v-if="comment.isPersonal === false && comment.commentStatus.id !== 1" class="checkImg">
                <img src="../../assets/tick.png" alt="" />
            </span>
            <p>{{ comment.content }}</p>
        </div>

        <div class="commentButton" v-if="commentIsNote">
            <button @click="removePersonalNote">{{ $t("commentPanel.removePersonalNote") }}</button>
        </div>

        <div class="commentAction" v-else>
            <a class="pointer" @click="toggleRepliesBloc">
                <span v-if="repliesBlocIsOpened">-</span> <span v-else>+</span> {{ $t("commentPanel.reply") }}
            </a>
            <a class="pointer" v-if="haveRights" @click="statusSwitcher">{{ resolved }}</a>
            <a class="pointer" v-if="haveRights" @click="InPdfSwitcher">{{ inPdf }}</a>
        </div>

        <div class="replies" v-if="comment.isPersonal === false" v-show="repliesBlocIsOpened">
            <div class="sub-comment" v-for="subComment in subComments" :key="subComment.id">
                <p>
                    <span class="ff-bold senderComment">{{ subComment.createdUser.initial }}</span> {{ subComment.content }}
                </p>
            </div>
            <textarea class="newComment" v-model="subNewCommentField" :placeholder="$t('commentPanel.replySubCommentPh')"></textarea>
            <RoundedButton :disabled="!canSaveReply" @click="sendReply"> {{ $t("commentPanel.replyButton") }} </RoundedButton>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import Toast from "vue-easy-toast";
import { mapGetters, mapActions } from "vuex";
import TryLoadMixin from "@/mixins/TryLoadMixin";
import commentService from "@/services/comment";
import RoundedButton from "@/catalog/components/rounded-button";
import cacheService from "@/services/cache";

Vue.use(Toast);
export default {
    name: "CommentBox",
    props: ["comment"],
    mixins: [TryLoadMixin],
    components: { RoundedButton },
    data() {
        return {
            repliesBlocIsOpened: false,
            subComments: [],
            subNewCommentField: "",
            colorClient: cacheService.getLinkColor()
        };
    },
    computed: {
        ...mapGetters("currentUser", ["currentUser"]),
        ...mapGetters("comment", {
            status: "status",
            personalNotes: "personalNotes"
        }),
        lang() {
            return cacheService.getUserLang();
        },
        commentIsNote() {
            return this.comment.isPersonal === true;
        },
        commentTitle() {
            return this.comment.isPersonal === true
                ? this.$t("commentPanel.personalNoteTitle")
                : this.comment.createdUser.firstname + " " + this.comment.createdUser.lastname;
        },
        resolved() {
            if (this.status.openStatus) {
                return this.comment.commentStatus.id !== 1 ? this.$t("commentPanel.asOpened") : this.$t("commentPanel.asClosed");
            }
        },
        inPdf() {
            return this.comment.printInPdf === true ? this.$t("commentPanel.excludePdf") : this.$t("commentPanel.includePdf");
        },
        haveRights() {
            return this.comment.createdUser.id === this.currentUser.user.id;
        },
        canSaveReply() {
            return Boolean(this.subNewCommentField);
        }
    },
    methods: {
        ...mapActions("comment", {
            updateCommentS: "updateCommentS",
            removeCommentS: "removeCommentS"
        }),
        ...mapActions("events/comments", ["dispatcherAddComment", "dispatcherRemoveComment"]),
        async toggleRepliesBloc() {
            if (this.repliesBlocIsOpened) {
                this.repliesBlocIsOpened = false;
                return;
            }
            this.subComments = await this.tryLoad(() => commentService.getReplies(this.$parent.appId, this.$parent.paramsGetter(), this.comment.id));
            this.repliesBlocIsOpened = true;
        },
        async sendReply() {
            if (!this.canSaveReply) return false;
            const subComment = {
                isPersonal: false,
                content: this.subNewCommentField,
                commentStatus: { id: 1 },
                parent: { id: this.comment.id }
            };
            const { commentData, appId } = this.$parent;
            Object.assign(subComment, commentData);
            const response = await this.tryLoad(() => commentService.postReply(appId, subComment));
            const { id: user } = this.currentUser.user;
            const UUID = this.$parent.commentData.componentIdExternal;
            const type = "PUBLIC";
            this.dispatcherAddComment({ user, type, UUID });
            this.subNewCommentField = "";
            this.subComments.push(response);
            this.dialogSuccess(this.$t("commentPanel.newCommentMessage"));
        },
        async removePersonalNote() {
            const res = await this.dialogConfirm(this.$t("commentPanel.confirmDelete"), this.$t("commentPanel.deleteAction"));
            if (res === true) {
                await this.tryLoad(() => this.removeCommentS(this.comment.id));
                this.dispatcherRemoveComment({
                    UUID: this.$parent.commentData.componentIdExternal,
                    left: this.personalNotes.filter(note => note.createdUser.id === this.currentUser.user.id).length,
                    type: "PRIVATE"
                });
            }
        },
        statusSwitcher() {
            this.comment.commentStatus.id = this.comment.commentStatus.id === 1 ? 2 : 1;
            this.updateComment();
        },
        InPdfSwitcher() {
            this.comment.printInPdf = !this.comment.printInPdf;
            this.updateComment();
        },
        async updateComment() {
            const { comment } = this;
            await this.tryLoad(() => this.updateCommentS({ comment }));
            this.dialogSuccess(this.$t("commentPanel.updateCommentMessage"));
        }
    }
};
</script>

<style lang="scss">
@import "../../assets/css/themes";
.commentBox {
    margin-bottom: 0;
    margin-top: 0;
    border-bottom: 1px #000 dashed;
    padding: 10px;
    text-transform: initial;

    .title {
        font-size: 11px;
        border-bottom: 1px #efefef solid;
        padding: 3px;
        margin-bottom: 10px;
        font-family: "Roboto", sans-serif;

        .date {
            float: right;
            font-weight: normal;
            font-size: 11px;
        }
    }

    .commentContent {
        p {
            margin: 0px;
            text-align: justify;
            line-height: 14px;
            font-size: 13px;
            word-break: break-word;
            vertical-align: middle;
            display: inline-block;
            width: 90%;
            padding: 5px;
            background-color: #f9f9f9;
        }

        .checkImg {
            display: inline-block;
            vertical-align: middle;

            img {
                width: 15px;
            }
        }
    }

    .commentAction {
        margin: 5px;
        margin-left: 0px;

        & > * {
            display: inline-block;
            text-decoration: underline;
            margin-right: 15px;
            font-weight: bold;
            text-transform: uppercase;
            font-size: 10px;
            color: #a8a8a8;
        }
    }

    .replies {
        margin-top: 20px;
        padding-left: 30px;

        .sub-comment {
            border-bottom: 1px #efefef solid;
            padding-bottom: 10px;
            margin-top: 10px;
            p {
                margin: 0px;
                font-size: 12px;

                .senderComment {
                    font-size: 12px;
                    color: #a8a8a8;
                    text-transform: capitalize;
                }
            }
        }

        textarea {
            font-size: 11px;
            display: inline-block;
            vertical-align: middle;
            width: 80%;
            color: #000000;
            border-bottom: 1px #000000 solid;
        }

        .btn {
            font-size: 13px;
            padding: 5px 0px;
            width: 15%;
            text-align: center;
            max-width: 60px;
            vertical-align: baseline;
            color: #000;
            border: 0px;
            font-size: 9px;
            background-color: #a8a8a8;
        }
    }
}
.commentBox.personalNote {
    background-color: rgba(0, 238, 255, 0.1);

    .title {
        border-bottom: 1px turquoise solid;
    }

    .commentContent {
        p {
            background-color: transparent;
        }
    }
}
.commentBox.resolved {
    background-color: rgb(197, 248, 214);

    .title {
        border-bottom: 1px turquoise solid;
    }
}
</style>



// WEBPACK FOOTER //
// CommentBox.vue?0b940e30